// Canonical BBS critical-factor item labels.
// ----------------------------------------------------------------------------
// Item labels reach analytics in whatever language and casing the source used:
//
//   * Forms filled in the app always store English — BBSO1Form pushes a
//     hardcoded English label, so the UI language never affects what is saved.
//   * Scanned / captured cards are transcribed by the OCR model, which echoes
//     the item names printed on the card. Spanish BBS cards therefore land in
//     the database in Spanish, and the model's English also differs in casing
//     from the digital form ("Manual lifting mechanics" vs "Manual Lifting
//     Mechanics", "(Line of Fire)" vs "(Line of fire)").
//
// Left alone these count as separate categories, so "Excavations" and
// "Excavaciones" each hold part of the same category's observations and both
// report the wrong rate. Everything is folded onto the canonical English label
// the digital form writes.

// Canonical labels, spelled exactly as BBSO1Form stores them.
const CANONICAL_LABELS = [
  'Eye & Face',
  'Hand',
  'Body',
  'Head',
  'Respiratory',
  'Hearing',
  'Proper Body Positioning (Line of fire)',
  'Manual Lifting Mechanics',
  'Repetition',
  'Pinch Points',
  'Pulling/Pushing',
  'Extending',
  'Slips, Trips, Falls',
  'Congested Work Area',
  'Barricades/Signage',
  'Excavations',
  'Fall Protection',
  'Spill Control',
  'JSA Cover Task',
  'Hazard Recognition',
  'Energy Isolation (LOTO)',
  'Simultaneous Operations (SIMOPS)',
  'Hand/Power Tools',
  'Mobile Equipment',
];

// Wordings seen in transcribed cards, grouped by the canonical label they mean.
// Comparison is accent- and case-insensitive and ignores punctuation, so only
// genuinely different wording needs an entry here.
const LABEL_ALIASES = {
  'Eye & Face': ['ojos y cara', 'ojos y rostro', 'proteccion de ojos y cara', 'eye face'],
  Hand: ['mano', 'manos', 'proteccion de manos'],
  Body: ['cuerpo', 'proteccion corporal', 'proteccion del cuerpo'],
  Head: ['cabeza', 'proteccion de la cabeza'],
  Respiratory: ['respiratorio', 'respiratoria', 'proteccion respiratoria'],
  Hearing: ['audicion', 'auditiva', 'proteccion auditiva', 'oido'],

  'Proper Body Positioning (Line of fire)': [
    'posicionamiento adecuado del cuerpo linea de fuego',
    'posicion adecuada del cuerpo linea de fuego',
    'posicionamiento correcto del cuerpo linea de fuego',
    'posicionamiento adecuado del cuerpo',
  ],
  'Manual Lifting Mechanics': [
    'mecanica de levantamiento manual',
    'mecanica de izaje manual',
    'levantamiento manual',
  ],
  Repetition: ['repeticion'],
  'Pinch Points': ['puntos de pellizco', 'puntos de pinzamiento', 'puntos de atrapamiento'],
  'Pulling/Pushing': ['tirar empujar', 'jalar empujar', 'halar empujar', 'empujar tirar'],
  Extending: ['prolongacion', 'extender', 'extension', 'estirarse', 'alcance excesivo'],

  'Slips, Trips, Falls': [
    'resbalones tropiezos caidas',
    'resbalones tropezones caidas',
    'resbalones traspies caidas',
    'resbalones tropiezos y caidas',
  ],
  'Congested Work Area': ['area de trabajo congestionada', 'zona de trabajo congestionada'],
  'Barricades/Signage': ['barricadas senalizacion', 'barreras senalizacion', 'barricadas senales'],
  Excavations: ['excavaciones', 'excavacion'],
  'Fall Protection': ['proteccion contra caidas', 'proteccion anticaidas'],
  'Spill Control': ['control de derrames', 'control de derrame'],

  'JSA Cover Task': [
    'tarea de cobertura jsa',
    'jsa cubre la tarea',
    'el jsa cubre la tarea',
    'tarea cubierta por jsa',
  ],
  'Hazard Recognition': [
    'reconocimiento de peligros',
    'reconocimiento de riesgos',
    'identificacion de peligros',
  ],
  'Energy Isolation (LOTO)': [
    'aislamiento de energia loto',
    'aislamiento energetico loto',
    'bloqueo y etiquetado loto',
    'aislamiento de energia',
  ],
  'Simultaneous Operations (SIMOPS)': [
    'operaciones simultaneas simops',
    'operaciones simultaneas',
    'simultaneous operations',
  ],
  'Hand/Power Tools': [
    'herramientas manuales electricas',
    'herramientas manuales y electricas',
    'herramientas de mano electricas',
  ],
  'Mobile Equipment': ['equipos moviles', 'equipo movil'],
};

// Rows that fall back to raw json_data carry the bucket in the text
// ("Work Environment - Excavations"); the item alone is what identifies it.
const BUCKET_PREFIX = /^\s*(PPE|Body Position|Work Environment|Work Procedures?)\s*[-\u2013\u2014]\s*/i;

// Strips the bucket prefix, the printed item letter ("A. Excavations"), accents,
// punctuation and casing so wording is all that is compared.
const normalizeKey = (label) =>
  String(label || '')
    .replace(BUCKET_PREFIX, '')
    .replace(/^\s*[A-Fa-f0-9]{1,2}[.)]\s*/, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const LOOKUP = (() => {
  const map = new Map();
  CANONICAL_LABELS.forEach((canonical) => {
    map.set(normalizeKey(canonical), canonical);
    (LABEL_ALIASES[canonical] || []).forEach((alias) => {
      map.set(normalizeKey(alias), canonical);
    });
  });
  return map;
})();

// Returns the canonical English label for a BBS item. Labels that are not a
// known critical factor (free-text or newly added items) pass through trimmed
// and unchanged, so nothing is silently dropped from the analytics.
export const canonicalCategoryLabel = (label) => {
  const raw = String(label || '').trim();
  if (!raw) return raw;
  return LOOKUP.get(normalizeKey(raw)) || raw;
};

export default canonicalCategoryLabel;
